import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, MailCheck } from "lucide-react";
import { api, apiErrorMessage } from "../api";
import { useAuth } from "../context/AuthContext";

// Module-level guard so React 18 StrictMode / double-mount doesn't double-consume the token
const consumed = new Set();

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const [state, setState] = useState({ status: "loading" });
  const ranRef = useRef(false);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState({ status: "error", message: "Missing verification token." }); return; }
    if (ranRef.current || consumed.has(token)) return;
    ranRef.current = true; consumed.add(token);

    (async () => {
      try {
        const r = await api.verifyEmail(token);
        await refreshMe?.();
        setState({ status: "ok", email: r.email });
      } catch (ex) {
        setState({ status: "error", message: apiErrorMessage(ex, "Verification failed.") });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-forest text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center space-y-4" data-testid="verify-email">
        {state.status === "loading" && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-coral" />
            <h1 className="font-display text-3xl">Verifying…</h1>
          </>
        )}
        {state.status === "ok" && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-emerald-300" />
            <h1 className="font-display text-3xl">Email verified.</h1>
            <p className="text-white/70 text-sm">Thanks for confirming <b>{state.email}</b>.</p>
            <div className="pt-2">
              <button onClick={() => nav("/app")} className="btn-coral px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2">
                <MailCheck size={14} /> Go to Full Bench
              </button>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={40} className="mx-auto text-coral" />
            <h1 className="font-display text-3xl">Verification failed</h1>
            <p className="text-white/70 text-sm">{state.message}</p>
            <p className="text-white/60 text-xs">
              Sign in and hit <Link to="/app/settings" className="text-coral underline">Settings → Resend verification email</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
